import {
  computeExpectedNet,
  computeExpectedVat,
  Decimal,
  ZERO,
  differsBeyondTolerance
} from '@insio/decimal';
import type { InternalVatRow } from './vat-summary-types';

export interface VatRowExpectations {
  /** netAmount × vatRate / 100. ZERO when vatRate is 0. */
  expectedVat: Decimal;
  /** vatAmount × 100 / vatRate. ZERO when vatRate is 0. */
  expectedNet: Decimal;
  /** True when the typed vatAmount disagrees with `expectedVat` beyond tolerance. */
  hasVatMismatch: boolean;
  /** True when the typed netAmount disagrees with `expectedNet` beyond tolerance. */
  hasNetMismatch: boolean;
  /** Convenience: `hasVatMismatch || hasNetMismatch`. */
  hasMismatch: boolean;
}

/** Line-derived expected net/vat for a rate, fetched via `invoiceComputedVatSummary`. */
export interface ExpectedFromLines {
  netAmount: Decimal;
  vatAmount: Decimal;
}

/**
 * Per-row expected-amount derivation used by the VAT summary editor to drive
 * the "expected value" hints next to each amount input.
 *
 * When `expectedFromLines` is given (the backend line-derived summary for this
 * rate), both expected amounts come from the lines and the typed values are
 * compared against them. Otherwise the row's own net↔vat math is used.
 *
 * 0 % rows fall back to ZEROs/`false` only in the local-math branch: VAT must
 * be zero by invariant (enforced by `useVatRowEditor`), so back-deriving the
 * net from a zero VAT is undefined.
 */
export function computeVatRowExpectations(
  row: Pick<InternalVatRow, 'vatRate' | 'netAmount' | 'vatAmount'>,
  expectedFromLines?: ExpectedFromLines | null
): VatRowExpectations {
  if (expectedFromLines) {
    const { netAmount: expectedNet, vatAmount: expectedVat } = expectedFromLines;
    const hasVatMismatch = differsBeyondTolerance(row.vatAmount, expectedVat);
    const hasNetMismatch = differsBeyondTolerance(row.netAmount, expectedNet);
    return {
      expectedVat,
      expectedNet,
      hasVatMismatch,
      hasNetMismatch,
      hasMismatch: hasVatMismatch || hasNetMismatch
    };
  }
  if (row.vatRate === 0) {
    return {
      expectedVat: ZERO,
      expectedNet: ZERO,
      hasVatMismatch: false,
      hasNetMismatch: false,
      hasMismatch: false
    };
  }
  const vatRate = new Decimal(row.vatRate);
  const expectedVat = computeExpectedVat(row.netAmount, vatRate) ?? ZERO;
  const expectedNet = computeExpectedNet(row.vatAmount, vatRate) ?? ZERO;
  const hasVatMismatch = differsBeyondTolerance(row.vatAmount, expectedVat);
  const hasNetMismatch = differsBeyondTolerance(row.netAmount, expectedNet);
  return {
    expectedVat,
    expectedNet,
    hasVatMismatch,
    hasNetMismatch,
    hasMismatch: hasVatMismatch || hasNetMismatch
  };
}
